import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { DetalleDelExperimento } from "@/components/laboratorio/DetalleDelExperimento";
import { NuevoExperimentoDialog } from "@/components/laboratorio/NuevoExperimentoDialog";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useExperimentos } from "@/hooks/useLaboratorio";
import { formatearFecha } from "@/lib/fechas";

/**
 * Pestaña «Experimentos»: el borrador de un prompt contra el publicado, sobre
 * casos congelados, con su calificación.
 *
 * El experimento abierto y la función a probar viajan en la búsqueda de la url
 * (`?experimento=5`, `?funcion=1&nuevo=1`): «Probar borrador» entra por ahí.
 */
export function ExperimentosDelLaboratorio() {
  const [params, setParams] = useSearchParams();
  const funcion = Number(params.get("funcion")) || undefined;
  const experimentos = useExperimentos(funcion);

  const [dialogo, setDialogo] = useState(params.get("nuevo") === "1");

  /* La señal de «abrir el alta» se consume: si quedara en la url, recargar o
     volver atrás volvería a abrir un diálogo que nadie pidió. */
  useEffect(() => {
    if (params.get("nuevo") === "1") {
      const siguiente = new URLSearchParams(params);
      siguiente.delete("nuevo");
      setParams(siguiente, { replace: true });
    }
  }, [params, setParams]);

  const lista = experimentos.data ?? [];
  const elegidoId = Number(params.get("experimento")) || lista[0]?.id || null;

  function abrirExperimento(id: number) {
    const siguiente = new URLSearchParams(params);
    siguiente.set("experimento", String(id));
    siguiente.delete("nuevo");
    setParams(siguiente);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {lista.length > 0 ? (
          <div className="w-full max-w-md">
            <NativeSelect
              aria-label="Experimento"
              size="compacto"
              value={elegidoId ?? ""}
              onChange={(e) => abrirExperimento(Number(e.target.value))}
            >
              {lista.map((e) => (
                <option key={e.id} value={e.id}>
                  {`#${e.id} · ${e.funcion.nombre} · ${e.item_codigo} · ${formatearFecha(e.iniciado_en)}`}
                </option>
              ))}
            </NativeSelect>
          </div>
        ) : null}
        <Button type="button" variant="outline" className="ml-auto rounded-[0.75rem] text-[0.78125rem] font-bold" onClick={() => setDialogo(true)}>
          Nuevo experimento
        </Button>
      </div>

      {experimentos.isPending ? (
        <Skeleton className="h-64 rounded-[1.25rem]" />
      ) : elegidoId === null ? (
        <p className="text-meta text-ink-medio border-border rounded-[1.25rem] border-[1.5px] px-5 py-10 text-center">
          Todavía no hay experimentos. Prueba el borrador de un prompt contra la versión publicada.
        </p>
      ) : (
        <DetalleDelExperimento key={elegidoId} id={elegidoId} />
      )}

      <NuevoExperimentoDialog
        abierto={dialogo}
        funcionInicial={funcion ?? null}
        onCerrar={() => setDialogo(false)}
        onLanzado={abrirExperimento}
      />
    </div>
  );
}
